import React, { useCallback, useState } from "react";
import { Alert } from "@jugl-web/ui-components";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { CRMAccountTile } from "../CRMAccountTile";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { ReactComponent as GmailBorderedIcon } from "./assets/gmail.svg";
import { ReactComponent as RemoveIcon } from "./assets/remove.svg";
import defaultAvatar from "./assets/default-avatar.png";
import { useEmailAccount } from "../../../../providers/EmailAccountProvider/useEmailAccount";
import { useManageAccountsDrawer } from "../../providers/ManageAccountsDrawerContext";

export const GmailAccountCard: React.FC<{
  isClickable?: boolean;
}> = ({ isClickable }) => {
  const { t } = useTranslations();
  const { entity } = useEntitySelectedProvider();
  const { toast } = useToast();
  const { navigateToPage } = useNavigation();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();

  const {
    emailAccount,
    isConnected,
    connectEmailAccount,
    disconnectEmailAccount,
    isDisconnectingEmailAccount,
  } = useEmailAccount();
  const { openManageAccountDrawer, isInboxModule } = useManageAccountsDrawer();

  const [isDisconnectAlertOpen, setIsDisconnectAlertOpen] = useState(false);

  const handleDisconnectAccount = useCallback(async () => {
    try {
      await disconnectEmailAccount();
      toast(
        t({
          id: "client-support-page.gmail-account-disconnected",
          defaultMessage: "Gmail account disconnected successfully",
        })
      );
      openManageAccountDrawer(undefined);
      if (isInboxModule) {
        navigateToPage("clientSupport");
      }
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  }, [
    disconnectEmailAccount,
    isInboxModule,
    navigateToPage,
    openManageAccountDrawer,
    t,
    toast,
  ]);

  const handleClick = () => {
    if (!isClickable) {
      return;
    }
    if (!isConnected) {
      connectEmailAccount();
    } else {
      transitionTo({ name: "emailAccount" });
    }
  };

  return (
    <>
      <CRMAccountTile
        title={
          emailAccount?.email ||
          t({
            id: "client-support-page.gmail",
            defaultMessage: "Gmail",
          })
        }
        isConnected={isConnected}
        onClick={handleClick}
        img={entity.logoImg || undefined}
        defaultAvatar={defaultAvatar}
        icon={GmailBorderedIcon}
        status={isConnected ? "accountConnected" : "accountDisconnected"}
        description={
          isConnected
            ? t({
                id: "client-support-page.gmail-account-connected-tile",
                defaultMessage:
                  "Emails from your Customers will appear in the Inbox 📥",
              })
            : t({
                id: "client-support-page.gmail-account-disconnected-tile",
                defaultMessage:
                  "Connect your Gmail Account to receive and reply to customer Emails right from the Inbox 📥",
              })
        }
        menuItems={[
          [
            {
              id: "disconnect-account",
              label: t({
                id: "client-support-page.disconnect-account",
                defaultMessage: "Disconnect Account",
              }),
              icon: <RemoveIcon />,
              onSelect: () => setIsDisconnectAlertOpen(true),
              isDisabled: isDisconnectingEmailAccount,
            },
          ],
        ]}
      />
      <Alert
        isOpen={isDisconnectAlertOpen}
        onRequestClose={() => setIsDisconnectAlertOpen(false)}
        title={t({
          id: "client-support-page.disconnect-account",
          defaultMessage: "Disconnect Account",
        })}
        content={t(
          {
            id: "client-support-page.disconnect-gmail-account",
            defaultMessage:
              "Are you sure you want to disconnect {email} Gmail account?",
          },
          {
            email: (
              <span className="font-medium text-primary">
                {emailAccount?.email || ""}
              </span>
            ),
          }
        )}
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            color: "grey",
            role: "close",
          },
          {
            text: t({
              id: "client-support-page.disconnect",
              defaultMessage: "Disconnect",
            }),
            onClick: async (_, { closeAlert }) => {
              closeAlert();
              await handleDisconnectAccount();
            },
          },
        ]}
      />
    </>
  );
};
